import { useEffect, useMemo, useRef, useState } from "react";
import { SheetDialog } from "./SheetDialog";
import { WheelColumn, type WheelHandle } from "./WheelColumn";
import { DirectInput, fmtSeg, initVals, pad, roundStep, segMin, type Seg } from "./TimePickerInput";
import css from "./TimePicker.module.css";

/**
 * TimePicker — React-порт design-references/time-picker.js по семантике.
 * Управляемый компонент: открытие через state хоста, роль-акцент — из
 * html[data-role] (var(--role) в CSS).
 *
 * Модель коммита ЯВНАЯ: «Готово» → onCommit("HH:MM" | "HH:MM–HH:MM");
 * backdrop / «Отмена» / Esc → onCancel (без коммита). Закрывает хост.
 *
 * Silent-правило (эталон TP:129-130): программные докрутки барабанов
 * (init, пресет, прямой ввод) НЕ меняют state через onUserChange —
 * значение пишет только жест пользователя.
 *
 * range (11.10.2026, владелец: «чтобы попросить конец, нужно нажимать отдельную
 * кнопку — это плохо себя показало»): две пары барабанов на одном экране —
 * начало (часы, минуты) и конец (часы, минуты), без переключателя «Начало | Конец».
 * Активная пара (последняя тронутая) — куда идут «Сейчас» и пресеты.
 */
export interface TimePickerPreset {
  label: string;
  v: string;
}

export interface TimePickerProps {
  open: boolean;
  label?: string;
  mode?: "single" | "range";
  /** "HH:MM" или "HH:MM–HH:MM" (en-dash или дефис). */
  value?: string;
  step?: 1 | 5 | 15;
  presets?: (string | TimePickerPreset)[];
  onCommit: (value: string) => void;
  onCancel: () => void;
  /** Выше экранных модалок, ниже NumPad. */
  zIndex?: number | string;
}

export function TimePicker({
  open,
  label = "Время",
  mode = "single",
  value,
  step = 1,
  presets,
  onCommit,
  onCancel,
  zIndex = "calc(var(--z-modal) + 40)",
}: TimePickerProps) {
  const range = mode === "range";
  const [vals, setVals] = useState<[Seg, Seg]>(() => initVals(value, step));
  const [seg, setSeg] = useState<0 | 1>(0);
  /* какое время сейчас вводят с клавиатуры — отдельно от активной пары: барабан, докрутившийся
     уже после тапа по шапке, не должен перекинуть ввод на другое время */
  const [editing, setEditing] = useState<0 | 1 | null>(null);
  const whRef = useRef<WheelHandle>(null);
  const wmRef = useRef<WheelHandle>(null);
  const ehRef = useRef<WheelHandle>(null);
  const emRef = useRef<WheelHandle>(null);

  const hh = useMemo(() => Array.from({ length: 24 }, (_, h) => pad(h)), []);
  const mm = useMemo(() => Array.from({ length: 60 / step }, (_, i) => pad(i * step)), [step]);

  const spinTo = (s: Seg, animated: boolean, i: 0 | 1 = 0) => {
    (i === 0 ? whRef : ehRef).current?.setValue(pad(s.h), animated);
    (i === 0 ? wmRef : emRef).current?.setValue(pad(s.m), animated);
  };

  /* Реинициализация на каждое открытие; value/step фиксируются в момент open.
     Явный setValue покрывает быстрый reopen, пока шит ещё не размонтирован. */
  useEffect(() => {
    if (!open) return;
    const v = initVals(value, step);
    setVals(v);
    setSeg(0);
    setEditing(null);
    spinTo(v[0], false, 0);
    spinTo(v[1], false, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const setSegVal = (i: 0 | 1, s: Seg) => setVals((p) => (i === 0 ? [s, p[1]] : [p[0], s]));

  /* Для жестов барабанов — полевой функциональный апдейт: done() часов и
     минут могут попасть в один React-батч (rAF-коллбеки одного кадра), и
     сборка целого Seg из render-time vals затёрла бы первый апдейт вторым. */
  const setSegField = (i: 0 | 1, f: "h" | "m", v: number) =>
    setVals((p) => {
      const s = f === "h" ? { ...p[i], h: v } : { ...p[i], m: v };
      return i === 0 ? [s, p[1]] : [p[0], s];
    });

  const applyPreset = (v: string) => {
    const next = initVals(v, step);
    if (range && String(v).split(/[–-]/).length > 1) {
      setVals(next);
      spinTo(next[0], true, 0);
      spinTo(next[1], true, 1);
    } else {
      setSegVal(seg, next[0]);
      spinTo(next[0], true, seg);
    }
  };

  const doneEdit = (s: Seg | null) => {
    const i = editing ?? seg;
    setEditing(null);
    if (s) {
      setSeg(i);
      setSegVal(i, s);
      spinTo(s, true, i);
    }
  };

  /* Чип «Сейчас» всегда первый, дальше пресеты хоста (эталон TP:169-170). */
  const chips = useMemo<TimePickerPreset[]>(() => {
    const now = new Date();
    const list: TimePickerPreset[] = [
      { label: "Сейчас", v: `${pad(now.getHours())}:${pad(roundStep(now.getMinutes(), step))}` },
    ];
    for (const p of presets ?? []) list.push(typeof p === "string" ? { label: p, v: p } : p);
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presets, step, open]);

  const overMidnight = range && segMin(vals[1]) < segMin(vals[0]);
  const commit = () => onCommit(range ? `${fmtSeg(vals[0])}–${fmtSeg(vals[1])}` : fmtSeg(vals[0]));

  /* range: каждое время в шапке — своя кнопка прямого ввода */
  const segView = (i: 0 | 1) => editing === i ? <DirectInput seg={vals[i]} step={step} onDone={doneEdit} />
    : <button type="button" className={css.segbtnv} title="Ввести время с клавиатуры" onClick={() => { setSeg(i); setEditing(i); }}>{fmtSeg(vals[i])}</button>;
  const wheelPair = (i: 0 | 1) => (
    <div className={css.wheels}>
      <WheelColumn values={hh} initial={pad(vals[i].h)} onUserChange={(v) => { setSeg(i); setSegField(i, "h", Number(v)); }}
        handleRef={i === 0 ? whRef : ehRef} ariaLabel={range ? `${i === 0 ? "Начало" : "Конец"}, часы` : "Часы"} />
      <span className={css.colon}>:</span>
      <WheelColumn values={mm} initial={pad(vals[i].m)} onUserChange={(v) => { setSeg(i); setSegField(i, "m", Number(v)); }}
        handleRef={i === 0 ? wmRef : emRef} ariaLabel={range ? `${i === 0 ? "Начало" : "Конец"}, минуты` : "Минуты"} />
      <div className={css.selframe}>
        <span className={`${css.corner} ${css.tl}`} />
        <span className={`${css.corner} ${css.tr}`} />
        <span className={`${css.corner} ${css.bl}`} />
        <span className={`${css.corner} ${css.br}`} />
      </div>
    </div>
  );

  return (
    <SheetDialog open={open} onDismiss={onCancel} zIndex={zIndex} maxHeight="55dvh" ariaLabel={label}>
      <div className={css.head}>
        <span className={css.label}>{label}</span>
        {overMidnight ? <span className={css.badge}>за полночь</span> : null}
        {range ? (
          <span className={css.preview}>{segView(0)}<span className={css.dash}>–</span>{segView(1)}</span>
        ) : editing !== null ? (
          <span className={css.preview}><DirectInput seg={vals[0]} step={step} onDone={doneEdit} /></span>
        ) : (
          <button type="button" className={css.preview} title="Ввести время с клавиатуры" onClick={() => setEditing(0)}>
            <span className={css.segv}>{fmtSeg(vals[0])}</span>
          </button>
        )}
      </div>
      <div className={css.zone}>
        {range ? (
          <div className={css.pair}>
            {([0, 1] as const).map((i) => (
              <div key={i} className={`${css.group} ${seg === i ? css.on : ""}`} onPointerDown={() => { setSeg(i); setEditing(null); }}>
                <span className={css.groupLbl}>{i === 0 ? "Начало" : "Конец"}</span>
                {wheelPair(i)}
              </div>
            ))}
          </div>
        ) : wheelPair(0)}
        <div className={css.scan} />
      </div>
      <div className={css.presets}>
        {chips.map((p, i) => (
          <button key={`${i}-${p.label}`} type="button" className={css.chip} onClick={() => applyPreset(p.v)}>
            {p.label}
          </button>
        ))}
      </div>
      <div className={css.foot}>
        <button type="button" className={`${css.btn} ${css.cancel}`} onClick={onCancel}>
          Отмена
        </button>
        <button type="button" className={`${css.btn} ${css.go}`} onClick={commit}>
          Готово
        </button>
      </div>
    </SheetDialog>
  );
}
